/* InterRaterStudyPanel — Layer C admin dashboard.
 *
 * Lets an admin:
 *   1. Add a labelled case (module + case ref + AI verdict + clinician verdict)
 *   2. See the Cohen's κ, confusion matrix and binary metrics per module
 *   3. Browse / delete recent entries
 *
 * Design note: we intentionally do NOT store images here — only labels
 * and a case reference. This keeps the dataset GDPR-safe and is enough to
 * compute inter-rater agreement.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, Trash2, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";

const MODULES = [
  { key: "dental_xray", label_bg: "Dental X-ray" },
  { key: "mammography", label_bg: "Mammography" },
];

const LABELS_BG = {
  dental_xray: ["0 · без находки", "1 · леки", "2 · изразени / спешни"],
  mammography: ["0 · BI-RADS 1-2", "1 · BI-RADS 3", "2 · BI-RADS 4-5"],
};

function InterpretationPill({ interp }) {
  if (!interp) return <span className="text-xs text-gray-400">—</span>;
  const color = {
    almost_perfect: "#059669",
    substantial:    "#10B981",
    moderate:       "#F59E0B",
    fair:           "#F97316",
    slight:         "#EF4444",
    poor:           "#991B1B",
  }[interp.key] || "#6B7280";
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: `${color}15`, color }} data-testid={`interp-${interp.key}`}>
      {interp.label_bg}
    </span>
  );
}

function ModuleSummaryCard({ module, stats, isDark }) {
  const legend = LABELS_BG[module] || [];
  if (!stats || stats.n === 0) {
    return (
      <Card className={isDark ? "bg-gray-800 border-gray-700" : ""} data-testid={`ir-summary-${module}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold capitalize">{module.replace("_", " ")}</h4>
            <span className="text-xs text-gray-400">n = 0</span>
          </div>
          <p className="text-xs text-gray-400">Все още няма въведени случаи за този модул.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className={isDark ? "bg-gray-800 border-gray-700" : ""} data-testid={`ir-summary-${module}`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold capitalize">{module.replace("_", " ")}</h4>
          <span className="text-xs text-gray-500">n = {stats.n}</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-xs text-gray-500">Cohen's κ</div>
            <div className="text-2xl font-bold" style={{ color: "#0EA5E9" }} data-testid={`ir-kappa-${module}`}>
              {stats.kappa?.toFixed(3) ?? "—"}
            </div>
            {stats.kappa_ci95 && (
              <div className="text-[10px] text-gray-400">
                CI95 [{stats.kappa_ci95[0]}, {stats.kappa_ci95[1]}]
              </div>
            )}
          </div>
          <div>
            <div className="text-xs text-gray-500">Съгласие</div>
            <div className="text-2xl font-bold" style={{ color: "#10B981" }}>{stats.agreement_pct}%</div>
            <div className="text-[10px] text-gray-400">{stats.agreement_count} / {stats.n}</div>
          </div>
        </div>
        <InterpretationPill interp={stats.interpretation} />
        {stats.binary && (
          <div className="grid grid-cols-4 gap-2 pt-2 border-t text-center">
            {[["Sens", stats.binary.sensitivity], ["Spec", stats.binary.specificity], ["PPV", stats.binary.ppv], ["NPV", stats.binary.npv]].map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] text-gray-500">{k}</div>
                <div className="text-sm font-semibold">{(v * 100).toFixed(1)}%</div>
              </div>
            ))}
          </div>
        )}
        <div className="pt-2">
          <div className="text-[10px] text-gray-500 mb-1">Confusion (rows = AI, cols = клиницист)</div>
          <table className="text-[11px] w-full border-separate" style={{ borderSpacing: 2 }}>
            <thead>
              <tr>
                <th></th>
                {legend.map((l, i) => <th key={i} className="text-gray-400 font-normal">{i}</th>)}
              </tr>
            </thead>
            <tbody>
              {stats.confusion_matrix.map((row, ri) => (
                <tr key={ri}>
                  <td className="text-gray-400">{ri}</td>
                  {row.map((v, ci) => (
                    <td key={ci} className="text-center rounded"
                      style={{ background: ri === ci ? "#DCFCE7" : (v > 0 ? "#FEF3C7" : "#F3F4F6"),
                               color: ri === ci ? "#065F46" : (v > 0 ? "#78350F" : "#9CA3AF"),
                               padding: "4px 6px", fontWeight: v > 0 ? 600 : 400 }}>
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function InterRaterStudyPanel({ API, isDark }) {
  const [stats, setStats] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    module: "dental_xray",
    case_ref: "",
    ai_label: 0,
    clinician_label: 0,
    clinician_name: "",
    clinician_role: "dentist",
    notes: "",
  });

  const token = useMemo(() => localStorage.getItem("token") || sessionStorage.getItem("nutramilo_access_token") || "", []);
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: e }] = await Promise.all([
        axios.get(`${API}/admin/inter-rater/stats`, { headers: auth }),
        axios.get(`${API}/admin/inter-rater/entries?limit=50`, { headers: auth }),
      ]);
      setStats(s);
      setEntries(e.entries || []);
    } catch (e) {
      toast.error("Грешка при зареждане на inter-rater статистики");
    } finally {
      setLoading(false);
    }
  }, [API, auth]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.case_ref.trim() || !form.clinician_name.trim()) {
      toast.error("Case ref и име на клиницист са задължителни");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/inter-rater/entry`, {
        ...form,
        ai_label: parseInt(form.ai_label, 10),
        clinician_label: parseInt(form.clinician_label, 10),
      }, { headers: auth });
      toast.success("Случаят е добавен");
      setForm(f => ({ ...f, case_ref: "", notes: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Грешка при запис");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Изтриване на този случай?")) return;
    try {
      await axios.delete(`${API}/admin/inter-rater/entry/${id}`, { headers: auth });
      toast.success("Изтрито");
      load();
    } catch { toast.error("Грешка при изтриване"); }
  };

  return (
    <div className="space-y-4" data-testid="inter-rater-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Inter-rater agreement study</h3>
          <p className="text-xs text-gray-500">Layer C · AI verdict vs клиницист verdict · Cohen's κ + confusion matrix</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ir-refresh">
          <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Опресни
        </Button>
      </div>

      {/* Overall summary */}
      {stats?.overall && stats.overall.n > 0 && (
        <Card className={isDark ? "bg-gray-800 border-gray-700" : "bg-gradient-to-r from-sky-50 to-violet-50 border-sky-100"}>
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs text-gray-500">Общо (всички модули)</div>
              <div className="text-2xl font-bold text-[#0EA5E9]">κ = {stats.overall.kappa?.toFixed(3)}</div>
              <div className="text-xs text-gray-500">n = {stats.overall.n} · {stats.overall.agreement_pct}% съгласие</div>
            </div>
            <InterpretationPill interp={stats.overall.interpretation} />
          </CardContent>
        </Card>
      )}

      {/* Per-module summaries */}
      <div className="grid md:grid-cols-2 gap-4">
        {MODULES.map(m => (
          <ModuleSummaryCard key={m.key} module={m.key} stats={stats?.by_module?.[m.key]} isDark={isDark} />
        ))}
      </div>

      {/* Add entry form */}
      <Card className={isDark ? "bg-gray-800 border-gray-700" : ""} data-testid="ir-add-form">
        <CardContent className="p-4 space-y-3">
          <h4 className="font-semibold flex items-center gap-2"><Plus className="w-4 h-4" /> Добави случай</h4>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] text-gray-500">Модул</label>
              <select value={form.module} onChange={e => setForm(f => ({ ...f, module: e.target.value }))}
                className="w-full h-9 text-sm border rounded px-2" data-testid="ir-form-module">
                {MODULES.map(m => <option key={m.key} value={m.key}>{m.label_bg}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-gray-500">Case ref (анонимизиран)</label>
              <Input value={form.case_ref} onChange={e => setForm(f => ({ ...f, case_ref: e.target.value }))}
                placeholder="напр. 2026-01-042" className="h-9 text-sm" data-testid="ir-form-ref" />
            </div>
            <div>
              <label className="text-[11px] text-gray-500">Клиницист (име + роля)</label>
              <div className="flex gap-2">
                <Input value={form.clinician_name} onChange={e => setForm(f => ({ ...f, clinician_name: e.target.value }))}
                  placeholder="Д-р Лили Иванова" className="h-9 text-sm flex-1" data-testid="ir-form-clin-name" />
                <select value={form.clinician_role} onChange={e => setForm(f => ({ ...f, clinician_role: e.target.value }))}
                  className="h-9 text-sm border rounded px-2" data-testid="ir-form-clin-role">
                  <option value="dentist">dentist</option>
                  <option value="radiologist">radiologist</option>
                  <option value="oncologist">oncologist</option>
                  <option value="gp">GP</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-[11px] text-gray-500">AI verdict (0-2)</label>
              <select value={form.ai_label} onChange={e => setForm(f => ({ ...f, ai_label: e.target.value }))}
                className="w-full h-9 text-sm border rounded px-2" data-testid="ir-form-ai">
                {LABELS_BG[form.module].map((l, i) => <option key={i} value={i}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-gray-500">Clinician verdict (0-2)</label>
              <select value={form.clinician_label} onChange={e => setForm(f => ({ ...f, clinician_label: e.target.value }))}
                className="w-full h-9 text-sm border rounded px-2" data-testid="ir-form-clin">
                {LABELS_BG[form.module].map((l, i) => <option key={i} value={i}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-gray-500">Бележки</label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="по избор" className="h-9 text-sm" data-testid="ir-form-notes" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={submit} disabled={saving} className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid="ir-form-submit">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
              Добави
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent entries */}
      <Card className={isDark ? "bg-gray-800 border-gray-700" : ""}>
        <CardContent className="p-4">
          <h4 className="font-semibold mb-3">Последни случаи ({entries.length})</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500">
                <tr>
                  <th className="text-left py-1">Дата</th>
                  <th className="text-left">Модул</th>
                  <th className="text-left">Ref</th>
                  <th className="text-left">Клиницист</th>
                  <th>AI</th>
                  <th>Clin</th>
                  <th>Съгл.</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-gray-400 py-6">Все още няма случаи</td></tr>
                )}
                {entries.map(e => {
                  const agree = e.ai_label === e.clinician_label;
                  return (
                    <tr key={e.id} className="border-t" data-testid={`ir-entry-${e.id}`}>
                      <td className="py-1.5 text-gray-500">{new Date(e.created_at).toLocaleDateString("bg-BG")}</td>
                      <td>{e.module}</td>
                      <td className="font-mono">{e.case_ref}</td>
                      <td>{e.clinician_name} <span className="text-gray-400">· {e.clinician_role}</span></td>
                      <td className="text-center">{e.ai_label}</td>
                      <td className="text-center">{e.clinician_label}</td>
                      <td className="text-center">
                        {agree ? <CheckCircle2 className="w-3 h-3 text-green-600 inline" /> : <AlertTriangle className="w-3 h-3 text-amber-600 inline" />}
                      </td>
                      <td className="text-right">
                        <button onClick={() => del(e.id)} className="text-red-500 hover:text-red-700" data-testid={`ir-del-${e.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
